/**
 * "Which device is this report for?" (2026-10-06, mockup
 * claude.ai/artifact/3mjopH4LBGB5ZPDe9Vi2UU).
 *
 * A city report dropped or uploaded on a job files as the OFFICIAL report on
 * THAT job. When its serial names exactly one device on the job the server
 * files it with no question. Otherwise it is kept as a plain document and the
 * office gets this one card: the job's devices as big chips. Picking one turns
 * that document into the device's official report in place
 * (POST /api/attachments/:id/make-official), and a packet waiting only on that
 * report goes out on its own.
 *
 * The same hook backs "Make official" on a plain document already sitting on
 * the job (the #072654693 / #072658282 files).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Gauge, GitBranch, Hand, MapPin } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

/** A device on the job, as the server's JobDeviceCandidate sends it. */
export interface ReportDeviceCandidate {
    assetId: string;
    serial: string | null;
    label: string;
    assemblyType?: string | null;
    size?: string | null;
    make?: string | null;
    location?: string | null;
    /** A bypass riding its detector on this job — shown as a note, never a choice. */
    ridesAssetId?: string | null;
    /** An official report is already on this job for it. */
    filed?: boolean;
}

type Release = "released" | "not_parked" | "waiting" | "failed";

type MakeOfficialResponse =
    | {
          routed: "filed";
          attachmentId: string;
          assetId: string | null;
          serial: string;
          fileName: string;
          release: Release;
      }
    | { routed: "pick"; attachmentId: string; candidates: ReportDeviceCandidate[] };

/** The toast after a report files. The packet line is separate and never claims a send that failed. */
export function filedToastText(serial: string, release: Release): { text: string; failed: boolean } {
    const who = serial && serial !== "—" ? `S/N ${serial}` : "this job";
    if (release === "released") return { text: `Filed to ${who}. Last report in — packet sent.`, failed: false };
    if (release === "failed")
        return {
            text: `Filed to ${who}, but the packet didn't reach anyone. It's still held — resend it.`,
            failed: true,
        };
    if (release === "waiting")
        return { text: `Filed to ${who}. The packet is still waiting on another report.`, failed: false };
    return { text: `Filed as the official report for ${who}.`, failed: false };
}

/** Riders (a bypass on its DCDA) are a note; everything else is a chip. */
export function splitCandidates(candidates: ReportDeviceCandidate[]): {
    chips: ReportDeviceCandidate[];
    riders: Array<{ rider: ReportDeviceCandidate; parent: ReportDeviceCandidate | undefined }>;
} {
    const chips = candidates.filter((c) => !c.ridesAssetId);
    const riders = candidates
        .filter((c) => !!c.ridesAssetId)
        .map((rider) => ({ rider, parent: candidates.find((c) => c.assetId === rider.ridesAssetId) }));
    return { chips, riders };
}

interface Ask {
    attachmentId: string;
    fileName: string;
    candidates: ReportDeviceCandidate[];
}

export function useMakeOfficial(jobId: string, opts: { onDone?: () => void } = {}) {
    const qc = useQueryClient();
    const [ask, setAsk] = useState<Ask | null>(null);
    const [picked, setPicked] = useState<string | null>(null);

    const refresh = () => {
        // ["job", jobId] covers documents, official reports, packet status and the finish line.
        qc.invalidateQueries({ queryKey: ["job", jobId] });
        qc.invalidateQueries({ queryKey: ["close-out-queue"] });
        qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
        opts.onDone?.();
    };

    const run = useMutation({
        mutationFn: (v: { attachmentId: string; fileName: string; assetId?: string }) =>
            api.post<MakeOfficialResponse>(
                `/api/attachments/${v.attachmentId}/make-official`,
                v.assetId ? { assetId: v.assetId } : {},
            ),
        onSuccess: (res, v) => {
            if (res.routed === "pick") {
                setPicked(null);
                setAsk({ attachmentId: v.attachmentId, fileName: v.fileName, candidates: res.candidates });
                return;
            }
            setAsk(null);
            refresh();
            const t = filedToastText(res.serial, res.release);
            if (t.failed) toast.error(t.text, { duration: 12_000 });
            else toast.success(t.text);
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't make that the official report.");
        },
    });

    /** "Make official" on a file: files by serial, or opens the card. */
    const start = (file: { id: string; file_name: string }) =>
        run.mutate({ attachmentId: file.id, fileName: file.file_name });

    /** Open the card straight away (the upload already returned the devices). */
    const askFor = (attachmentId: string, fileName: string, candidates: ReportDeviceCandidate[]) => {
        setPicked(null);
        setAsk({ attachmentId, fileName, candidates });
    };

    const element = (
        <ReportDevicePickerDialog
            ask={ask}
            picked={picked}
            onPick={setPicked}
            busy={run.isPending}
            onClose={() => setAsk(null)}
            onFile={() => {
                if (ask && picked)
                    run.mutate({ attachmentId: ask.attachmentId, fileName: ask.fileName, assetId: picked });
            }}
        />
    );

    return { start, askFor, element, pending: run.isPending };
}

function ReportDevicePickerDialog({
    ask,
    picked,
    onPick,
    busy,
    onClose,
    onFile,
}: {
    ask: Ask | null;
    picked: string | null;
    onPick: (assetId: string) => void;
    busy: boolean;
    onClose: () => void;
    onFile: () => void;
}) {
    const { chips, riders } = splitCandidates(ask?.candidates ?? []);
    return (
        <Dialog open={!!ask} onClose={onClose} title="Which device is this report for?" className="max-w-2xl">
            <div className="flex flex-col gap-4" data-testid="report-device-picker">
                <div className="flex items-center gap-2 text-sm">
                    <Hand className="h-4 w-4 shrink-0 text-orange-700 dark:text-orange-400" />
                    <span className="min-w-0 truncate font-semibold" title={ask?.fileName}>
                        {ask?.fileName}
                    </span>
                </div>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {chips.map((d) => {
                        const sel = picked === d.assetId;
                        return (
                            <button
                                key={d.assetId}
                                type="button"
                                disabled={d.filed || busy}
                                onClick={() => onPick(d.assetId)}
                                aria-pressed={sel}
                                title={d.filed ? "An official report is already on this job for it" : d.label}
                                className={cn(
                                    "relative flex min-h-[96px] items-start gap-3 rounded-xl border-2 bg-muted/40 p-3 text-left transition-colors",
                                    sel
                                        ? "border-emerald-600 bg-emerald-50 dark:bg-emerald-950/40"
                                        : "border-border hover:border-primary",
                                    d.filed && "cursor-default opacity-45 hover:border-border",
                                )}
                            >
                                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg border border-border bg-card">
                                    <Gauge className="h-7 w-7 text-foreground/70" />
                                </span>
                                <span className="flex min-w-0 flex-col gap-0.5">
                                    {d.size && <span className="text-xl font-extrabold leading-none">{d.size}</span>}
                                    <span className="text-[13px] font-semibold">
                                        {[d.assemblyType?.toUpperCase(), d.make].filter(Boolean).join(" · ") || d.label}
                                    </span>
                                    {d.serial && (
                                        <span className="text-xs tabular-nums text-muted-foreground">
                                            SN {d.serial}
                                        </span>
                                    )}
                                    {d.location && (
                                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <MapPin className="h-3 w-3 shrink-0" />
                                            <span className="truncate">{d.location}</span>
                                        </span>
                                    )}
                                </span>
                                {d.filed && (
                                    <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">
                                        <CheckCircle2 className="h-3 w-3" /> Filed
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    {riders.map(({ rider, parent }) => (
                        <div
                            key={rider.assetId}
                            className="col-span-full flex items-center gap-2 pl-1 text-[13px] text-muted-foreground"
                        >
                            <GitBranch className="h-4 w-4 shrink-0" />
                            <span>
                                Bypass{rider.serial ? ` SN ${rider.serial}` : ""} rides the{" "}
                                {[parent?.size, parent?.assemblyType?.toUpperCase()].filter(Boolean).join(" ") ||
                                    "detector"}
                                . Pick that one.
                            </span>
                        </div>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button disabled={!picked || busy} onClick={onFile}>
                        File as official report
                    </Button>
                    <Button variant="outline" onClick={onClose} disabled={busy}>
                        It&apos;s not a report
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
